import { $api } from '@service/RequestService';
import { useQuery, useQueryClient } from 'react-query';

import { crmConstants, crmModels } from '..';

export const useLeadMapView = (
  props: {
    sourceLocationId?: number | null;
    minLongitude?: number | null;
    minLatitude?: number | null;
    maxLongitude?: number | null;
    maxLatitude?: number | null;
    pipeId?: number | null;
  } & Record<string, any>
) => {
  const { maxLatitude, maxLongitude, minLatitude, minLongitude, pipeId, sourceLocationId, ...params } = props;
  const qc = useQueryClient();
  const { ...data } = useQuery<crmModels.LeadMapView>({
    queryKey: [crmConstants.queryKeys.LEAD_MAP_VIEW, props],
    queryFn: async () => {
      const res = await $api.post('/v1/lead/map-view', { ...props, sourceLocationId: undefined });

      return res?.data?.data;
    },
    enabled: !!minLongitude && !!minLatitude && !!maxLongitude && !!maxLatitude,
    keepPreviousData: true
  });

  const all = qc.getQueriesData<crmModels.LeadMapView>([
    crmConstants.queryKeys.LEAD_MAP_VIEW,
    { pipeId: props?.pipeId, sourceLocationId, ...params }
  ]);
  const allData = all?.reduce<{
    leads: Record<string, crmModels.LeadMapView['leads'][number]>;
    points: Record<string, crmModels.LeadMapView['points'][number]>;
  }>(
    (p, c) => {
      const leads = {
        ...p.leads,
        ...(c?.[1]?.leads || [])?.reduce<Record<string, crmModels.LeadMapView['leads'][number]>>(
          (p2, c2) => ({ ...p2, [`${c2?.latitude}${c2?.longitude}`]: c2 }),
          {}
        )
      };

      const points = {
        ...p.points,
        ...(c?.[1]?.points || [])?.reduce<Record<string, crmModels.LeadMapView['points'][number]>>(
          (p2, c2) => ({
            ...p2,
            [`${c2?.latitude}${c2?.longitude}`]: c2
          }),
          {}
        )
      };

      p = {
        leads,
        points
      };

      return p;
    },
    {
      leads: {},
      points: {}
    }
  );

  Object.keys(allData?.leads)?.forEach(key => {
    if (key in allData?.points) {
      delete allData?.points[key];
    }
  });

  return {
    ...data,
    allData: {
      leads: Object.values(allData?.leads),
      points: Object.values(allData?.points)
    }
  };
};
